/**
 * Msg 604 · Phase B — Global Attributes Library admin screen.
 *
 * Route: `/atlas/admin/attributes` (atlas_admin only)
 *
 * Lets admins define library entries once and reuse them across
 * folders. Rename here → every folder using the entry updates
 * immediately (server-side enrichment). Delete → value rows on
 * every folder are removed; folders themselves are NEVER deleted
 * (backend enforces via post-condition check).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, Loader2, Type, Hash, Calendar,
  ToggleLeft, List as ListIcon, Save, X, Info, Wand2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  invalidateAttributes,
  removeAttributeById,
  useAtlasAttributes,
} from "../atlasAttributesCache";


const ANSWER_TYPES = [
  { value: "text",   label: "Text",       icon: Type },
  { value: "number", label: "Number",     icon: Hash },
  { value: "date",   label: "Date",       icon: Calendar },
  { value: "bool",   label: "Yes / No",   icon: ToggleLeft },
  { value: "list",   label: "Pick list",  icon: ListIcon },
];


function typeMeta(t) {
  return ANSWER_TYPES.find((x) => x.value === t) || ANSWER_TYPES[0];
}


function AttributeRow({ attr, onEdit, onDelete }) {
  const meta = typeMeta(attr.answer_type);
  const Icon = meta.icon;
  return (
    <div
      data-testid={`attribute-row-${attr.id}`}
      className="group flex items-center gap-3 p-3 rounded-xl border border-tb-navy/10 bg-white/60 hover:border-tb-navy/25 transition-colors"
    >
      <div className="shrink-0 h-9 w-9 rounded-lg bg-tb-navy/5 flex items-center justify-center text-tb-navy">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div
            className="text-tb-navy text-sm font-semibold truncate"
            data-testid={`attribute-name-${attr.id}`}
          >
            {attr.name}
          </div>
          <span className="shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-hand uppercase tracking-wide bg-tb-navy/5 text-tb-navy/70 border border-tb-navy/10">
            {meta.label}
          </span>
          {attr.answer_type === "list" && (attr.options || []).length > 0 && (
            <span
              className="shrink-0 text-[10px] font-hand text-tb-navy/50"
              data-testid={`attribute-optcount-${attr.id}`}
            >
              · {(attr.options || []).length} options
            </span>
          )}
          <span
            className="ml-auto shrink-0 text-[10px] font-hand text-tb-navy/50"
            data-testid={`attribute-usage-${attr.id}`}
            title="Folders that currently store a value for this attribute"
          >
            used on {attr.usage_count || 0} folder{(attr.usage_count === 1) ? "" : "s"}
          </span>
        </div>
        {attr.description && (
          <div className="mt-0.5 text-xs font-hand text-tb-navy/60 truncate">
            {attr.description}
          </div>
        )}
        <div className="mt-0.5 text-[10px] font-mono text-tb-navy/40">
          key: {attr.key}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => onEdit(attr)}
          data-testid={`attribute-edit-${attr.id}`}
          className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-tb-navy/60 hover:bg-tb-navy/5"
          aria-label="Edit attribute"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(attr)}
          data-testid={`attribute-delete-${attr.id}`}
          className="h-8 w-8 rounded-lg inline-flex items-center justify-center text-red-600 hover:bg-red-50"
          aria-label="Delete attribute"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function AttributeForm({ initial, onSave, onCancel, busy }) {
  const editing = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [answerType, setAnswerType] = useState(initial?.answer_type || "text");
  const [description, setDescription] = useState(initial?.description || "");
  const [options, setOptions] = useState((initial?.options || []).join(", "));

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
    };
    if (!editing) payload.answer_type = answerType;
    if ((editing ? initial.answer_type : answerType) === "list") {
      payload.options = options
        .split(",").map((s) => s.trim()).filter(Boolean);
    }
    onSave(payload);
  };

  const isList = (editing ? initial.answer_type : answerType) === "list";

  return (
    <form
      onSubmit={submit}
      data-testid="attribute-form"
      className="p-4 rounded-2xl border border-tb-navy/20 bg-white space-y-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-tb-navy font-semibold">
          {editing ? "Edit attribute" : "New library attribute"}
        </h3>
        {editing && (
          <span className="text-[10px] font-hand text-tb-navy/50">
            answer type is immutable — delete + recreate to change
          </span>
        )}
      </div>
      <label className="block">
        <span className="text-xs font-hand text-tb-navy/70">Name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          required
          maxLength={64}
          data-testid="attribute-form-name"
          className="mt-1 w-full h-9 px-3 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy"
          placeholder="Airport Distance"
        />
      </label>
      {!editing && (
        <label className="block">
          <span className="text-xs font-hand text-tb-navy/70">Answer type</span>
          <select
            value={answerType}
            onChange={(e) => setAnswerType(e.target.value)}
            disabled={busy}
            data-testid="attribute-form-type"
            className="mt-1 w-full h-9 px-3 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy bg-white"
          >
            {ANSWER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
      )}
      {isList && (
        <label className="block">
          <span className="text-xs font-hand text-tb-navy/70">
            Options (comma-separated)
          </span>
          <input
            type="text"
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            disabled={busy}
            data-testid="attribute-form-options"
            className="mt-1 w-full h-9 px-3 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy"
            placeholder="Yes, No, Partial"
          />
        </label>
      )}
      <label className="block">
        <span className="text-xs font-hand text-tb-navy/70">
          Description <span className="text-tb-navy/40">(optional)</span>
        </span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={busy}
          rows={2}
          maxLength={280}
          data-testid="attribute-form-desc"
          className="mt-1 w-full min-h-[54px] px-3 py-2 rounded-lg border border-tb-navy/20 outline-none focus:border-tb-navy text-sm font-hand text-tb-navy resize-y"
          placeholder="Optional hint shown to advisors when they add this attribute."
        />
      </label>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="attribute-form-cancel"
          className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !name.trim()}
          data-testid="attribute-form-save"
          className="h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {editing ? "Save changes" : "Create attribute"}
        </button>
      </div>
    </form>
  );
}


function DeleteConfirmModal({ attr, onConfirm, onClose, busy }) {
  const [typed, setTyped] = React.useState("");
  React.useEffect(() => { if (attr) setTyped(""); }, [attr]);
  if (!attr) return null;
  // 2026-08-06 — matches the folder-delete confirmation contract.
  // Advisor types `yes` (case-insensitive, trimmed) to enable the
  // red Confirm button. Prevents mis-click deletes.
  const yesTyped = typed.trim().toLowerCase() === "yes";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
      data-testid="attribute-delete-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-10 w-10 rounded-full bg-red-50 text-red-600 inline-flex items-center justify-center">
            <Trash2 className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-tb-navy text-lg font-semibold">
              Delete "{attr.name}"?
            </h3>
            <p className="mt-2 text-xs font-hand text-tb-navy/70 leading-relaxed">
              This will remove this attribute from every folder that
              stores a value for it. <b>No folders will be deleted.</b>
              {(attr.usage_count || 0) > 0 && (
                <span className="block mt-2 text-red-700">
                  {attr.usage_count} folder{attr.usage_count === 1 ? "" : "s"} currently
                  use{attr.usage_count === 1 ? "s" : ""} this attribute — their values
                  will be permanently removed.
                </span>
              )}
            </p>
          </div>
        </div>
        <label className="block mt-4 text-xs font-hand text-tb-navy/80">
          Type <b>yes</b> to confirm deleting "{attr.name}":
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            disabled={busy}
            data-testid="attribute-delete-confirm-input"
            placeholder='yes'
            className={`mt-1 w-full rounded border px-2 py-1 text-sm outline-none ${
              yesTyped
                ? "border-emerald-500 bg-emerald-50/40"
                : "border-tb-navy/25 focus:border-tb-navy"
            }`}
          />
        </label>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="attribute-delete-cancel"
            className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !yesTyped}
            data-testid="attribute-delete-confirm"
            className="h-9 px-4 rounded-full bg-red-600 text-white font-hand text-xs disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete attribute
          </button>
        </div>
      </div>
    </div>
  );
}


export default function AtlasAttributesPage() {
  const attrs = useAtlasAttributes();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runningMigrate, setRunningMigrate] = useState(false);
  const [dedupePreview, setDedupePreview] = useState(null);
  const [dedupeBusy, setDedupeBusy] = useState(false);

  useEffect(() => {
    // Initial fetch — cache hook triggers, but ensure loaded even if
    // the module was hot-reloaded.
    invalidateAttributes();
  }, []);

  const filtered = useMemo(() => {
    return [...attrs].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" }));
  }, [attrs]);

  const handleSave = useCallback(async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/atlas/admin/attributes/${editing.id}`, payload);
        toast.success(`Updated "${payload.name}".`);
        setEditing(null);
      } else {
        await api.post("/atlas/admin/attributes", payload);
        toast.success(`Created "${payload.name}".`);
        setCreating(false);
      }
      invalidateAttributes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally {
      setBusy(false);
    }
  }, [editing]);

  const handleDelete = useCallback(async () => {
    if (!deleting) return;
    // Capture id BEFORE clearing state so the optimistic removal
    // still fires after the modal closes.
    const attrId = deleting.id;
    const attrName = deleting.name;
    setBusy(true);
    try {
      const r = await api.delete(`/atlas/admin/attributes/${attrId}`);
      const untagged = r.data?.value_facts_deleted || 0;
      toast.success(
        untagged
          ? `Deleted "${attrName}" — untagged from ${untagged} value row${untagged === 1 ? "" : "s"}.`
          : `Deleted "${attrName}".`
      );
      setDeleting(null);
      // 2026-08-06 ghost-attribute fix — splice from cache
      // synchronously so the row disappears the same tick, then
      // kick off a full refetch behind it for usage-count refresh.
      removeAttributeById(attrId);
      invalidateAttributes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }, [deleting]);

  const runMigration = useCallback(async () => {
    if (!window.confirm(
      "Run one-shot migration?\n\n" +
      "This will scan every folder's custom attributes, create library " +
      "entries for any that aren't already registered, and backfill " +
      "attribute_id on all matching fact rows. Idempotent — safe to " +
      "re-run.\n\nNo folders are deleted."
    )) return;
    setRunningMigrate(true);
    try {
      const r = await api.post("/atlas/admin/attributes/migrate");
      const s = r.data?.stats || {};
      toast.success(
        `Migration complete — ${s.library_entries_created || 0} entries created, ` +
        `${s.facts_backfilled || 0} facts backfilled.`
      );
      invalidateAttributes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Migration failed.");
    } finally {
      setRunningMigrate(false);
    }
  }, []);

  // Msg-612 Ask B — dedupe attribute facts across folders.
  // Two-step: dry-run to fetch a plan, then Confirm to commit.
  const startDedupe = useCallback(async () => {
    setDedupeBusy(true);
    try {
      const r = await api.post("/atlas/admin/dedupe-attribute-facts",
                                { dry_run: true });
      setDedupePreview(r.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Dedupe preview failed.");
    } finally {
      setDedupeBusy(false);
    }
  }, []);

  const commitDedupe = useCallback(async () => {
    if (!dedupePreview) return;
    if (!(dedupePreview.to_delete > 0)) {
      setDedupePreview(null);
      return;
    }
    setDedupeBusy(true);
    try {
      const r = await api.post("/atlas/admin/dedupe-attribute-facts",
                                { dry_run: false });
      const n = r.data?.deleted || 0;
      toast.success(`Dedupe complete — ${n} row${n === 1 ? "" : "s"} soft-deleted.`);
      setDedupePreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Dedupe failed.");
    } finally {
      setDedupeBusy(false);
    }
  }, [dedupePreview]);

  return (
    <div
      data-testid="attributes-admin-page"
      className="w-full max-w-4xl mx-auto p-6 md:p-8 space-y-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl text-tb-navy font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Attributes Library
          </h1>
          <p className="mt-2 text-sm font-hand text-tb-navy/70 leading-relaxed max-w-xl">
            Define custom attributes once — reuse them across every folder.
            Rename here and every folder shows the new label immediately.
            Delete here and the value is removed everywhere (folders themselves
            are never deleted).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runMigration}
            disabled={runningMigrate}
            data-testid="attributes-migrate-btn"
            title="One-shot import: scan existing per-folder attributes and register them in the library."
            className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {runningMigrate
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Info className="w-3.5 h-3.5" />}
            Import from existing folders
          </button>
          <button
            type="button"
            onClick={startDedupe}
            disabled={dedupeBusy}
            data-testid="attributes-dedupe-btn"
            title="Preview + soft-delete duplicate attribute rows (blank/filled pairs) across every folder."
            className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {dedupeBusy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Wand2 className="w-3.5 h-3.5" />}
            Dedupe attribute rows
          </button>
          <button
            type="button"
            onClick={() => { setCreating(true); setEditing(null); }}
            data-testid="attributes-new-btn"
            className="h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            New attribute
          </button>
        </div>
      </div>

      {(creating || editing) && (
        <AttributeForm
          initial={editing}
          busy={busy}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      <div className="space-y-2" data-testid="attributes-list">
        {filtered.length === 0 && (
          <div className="p-8 text-center rounded-2xl border border-dashed border-tb-navy/20 bg-white/40">
            <p className="text-sm font-hand text-tb-navy/60">
              No library attributes yet.
              {" "}
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="underline text-tb-navy"
                data-testid="attributes-empty-cta"
              >
                Add your first one
              </button>
              {" "}
              or import from existing folders using the button above.
            </p>
          </div>
        )}
        {filtered.map((a) => (
          <AttributeRow
            key={a.id}
            attr={a}
            onEdit={(x) => { setEditing(x); setCreating(false); }}
            onDelete={(x) => setDeleting(x)}
          />
        ))}
      </div>

      <DeleteConfirmModal
        attr={deleting}
        busy={busy}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />

      {dedupePreview && (
        <div
          data-testid="attributes-dedupe-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2
                  className="text-2xl text-tb-navy font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Dedupe attribute rows — preview
                </h2>
                <p className="mt-1 text-sm font-hand text-tb-navy/70">
                  Dry-run scan across every folder. Nothing has been
                  deleted yet — press Commit to soft-delete the rows
                  listed below.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDedupePreview(null)}
                data-testid="attributes-dedupe-close"
                className="text-tb-navy/60 hover:text-tb-navy"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <Stat label="Facts scanned" value={dedupePreview.facts_scanned || 0}
                    testid="dedupe-stat-scanned" />
              <Stat label="Groups"        value={dedupePreview.groups_examined || 0}
                    testid="dedupe-stat-groups" />
              <Stat label="Would delete"  value={dedupePreview.to_delete || 0}
                    testid="dedupe-stat-todelete"  highlight />
              <Stat label="Conflicts"     value={(dedupePreview.skipped_conflicts || []).length}
                    testid="dedupe-stat-conflicts" />
            </div>

            {(dedupePreview.delete_plan_sample || []).length > 0 && (
              <div className="max-h-56 overflow-auto border border-tb-navy/15 rounded-lg p-2 text-xs font-mono text-tb-navy/80"
                   data-testid="attributes-dedupe-plan">
                {dedupePreview.delete_plan_sample.map((p, i) => (
                  <div key={i} className="py-1 border-b border-tb-navy/5 last:border-b-0">
                    <span className="text-tb-navy/50">{p.reason}</span>
                    {" · "}
                    <span>{p.topic_slug}</span>
                    {" · "}
                    <span className="text-tb-navy">{p.attribute_key}</span>
                    {p.value ? <span className="text-tb-navy/50"> = "{String(p.value).slice(0,40)}"</span> : null}
                  </div>
                ))}
                {dedupePreview.delete_plan_total > (dedupePreview.delete_plan_sample || []).length && (
                  <div className="pt-2 text-tb-navy/50 italic">
                    …and {dedupePreview.delete_plan_total - dedupePreview.delete_plan_sample.length} more
                  </div>
                )}
              </div>
            )}

            {(dedupePreview.skipped_conflicts || []).length > 0 && (
              <div className="border border-amber-300 bg-amber-50/60 rounded-lg p-3 text-xs font-hand text-amber-900"
                   data-testid="attributes-dedupe-conflicts">
                <div className="font-semibold mb-1">
                  {(dedupePreview.skipped_conflicts || []).length} conflict{(dedupePreview.skipped_conflicts || []).length === 1 ? "" : "s"} skipped
                </div>
                <div className="text-amber-900/80">
                  These groups have multiple filled rows with different values.
                  We didn't touch them — resolve manually.
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDedupePreview(null)}
                data-testid="attributes-dedupe-cancel"
                className="h-9 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitDedupe}
                disabled={dedupeBusy || !(dedupePreview.to_delete > 0)}
                data-testid="attributes-dedupe-commit"
                className="h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {dedupeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {(dedupePreview.to_delete > 0)
                  ? `Commit — delete ${dedupePreview.to_delete} row${dedupePreview.to_delete === 1 ? "" : "s"}`
                  : "Nothing to delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-2 ${highlight ? "border-tb-navy bg-tb-navy/5" : "border-tb-navy/15 bg-white"}`}
    >
      <div className="text-2xl font-semibold text-tb-navy" style={{ fontFamily: '"Playfair Display", serif' }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-tb-navy/60 font-hand">
        {label}
      </div>
    </div>
  );
}
